import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { onValue, ref, set } from 'firebase/database';
import { Loader2, Plus, RefreshCcw, Save, Trash2 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { auth, database } from '../services/firebase';

type UserProfile = {
  displayName?: string;
  needsOrganizationSetup?: boolean;
  organizationId?: string;
  organizationMembershipStatus?: string;
  superAdmin?: boolean;
};

type CatalogRecord = Record<string, unknown>;

type AsyncStatus = 'idle' | 'saving' | 'success' | 'error';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null) {
    const candidate = error as { message?: unknown; details?: unknown; code?: unknown };
    if (typeof candidate.message === 'string' && candidate.message.trim()) {
      return candidate.message;
    }
    if (typeof candidate.details === 'string' && candidate.details.trim()) {
      return candidate.details;
    }
    if (typeof candidate.code === 'string' && candidate.code.trim()) {
      return `${fallback} (${candidate.code})`;
    }
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  return fallback;
}

const JSON_SPACING = 2;

type RootValueEditorProps = {
  catalogKey: string;
  value: unknown;
  isDarkMode: boolean;
  onSave: (nextValue: unknown) => Promise<void>;
};

function RootValueEditor({ catalogKey, value, isDarkMode, onSave }: RootValueEditorProps) {
  const [buffer, setBuffer] = useState(() => JSON.stringify(value, null, JSON_SPACING));
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setBuffer(JSON.stringify(value, null, JSON_SPACING));
  }, [value]);

  const handleReset = () => {
    setBuffer(JSON.stringify(value, null, JSON_SPACING));
    setStatus('idle');
    setErrorMessage(null);
  };

  const handleSave = async () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(buffer || 'null');
    } catch (error) {
      setStatus('error');
      setErrorMessage(getErrorMessage(error, 'Invalid JSON. Make sure the value is valid JSON.'));
      return;
    }
    setStatus('saving');
    setErrorMessage(null);
    try {
      await onSave(parsed);
      setStatus('success');
      window.setTimeout(() => setStatus('idle'), 1500);
    } catch (error) {
      setStatus('error');
      setErrorMessage(getErrorMessage(error, 'Failed to update catalog entry.'));
    }
  };

  const cardClass = isDarkMode ? 'bg-slate-900/60 text-slate-100 ring-slate-800' : 'bg-slate-100 text-slate-900 ring-slate-200';
  const buttonPrimaryClass = isDarkMode
    ? 'inline-flex items-center gap-2 rounded-lg bg-blue-500 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-slate-900'
    : 'inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-white';
  const buttonSecondaryClass = isDarkMode
    ? 'inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-1.5 text-sm font-semibold text-slate-200 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-600 focus:ring-offset-2 focus:ring-offset-slate-900'
    : 'inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-white';

  return (
    <div className={`rounded-2xl p-4 ring-1 ${cardClass}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold uppercase tracking-wide text-blue-400">Root Value</h4>
          <p className={`${isDarkMode ? 'text-slate-300' : 'text-slate-500'} text-xs`}>
            Edit the full JSON stored at <code className="font-mono text-xs">catalogs/{catalogKey}</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={handleReset} className={buttonSecondaryClass} disabled={status === 'saving'}>
            <RefreshCcw className="h-4 w-4" aria-hidden="true" />
            Reset
          </button>
          <button type="button" onClick={handleSave} className={buttonPrimaryClass} disabled={status === 'saving'}>
            {status === 'saving' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            Save
          </button>
        </div>
      </div>
      <textarea
        className={`${isDarkMode ? 'bg-slate-950 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200'} h-48 w-full rounded-xl border-none px-3 py-2 font-mono text-sm leading-6 ring-1 focus:outline-none focus:ring-2 focus:ring-blue-400`}
        value={buffer}
        onChange={event => setBuffer(event.target.value)}
        spellCheck={false}
      />
      {status === 'success' ? (
        <p className="mt-2 text-sm font-medium text-green-500">Saved.</p>
      ) : null}
      {status === 'error' && errorMessage ? (
        <p className="mt-2 text-sm font-medium text-rose-500">{errorMessage}</p>
      ) : null}
    </div>
  );
}

type CatalogChildEditorProps = {
  catalogKey: string;
  childKey: string;
  value: unknown;
  isDarkMode: boolean;
  onSave: (nextValue: unknown) => Promise<void>;
  onRemove: () => Promise<void>;
};

function CatalogChildEditor({ catalogKey, childKey, value, isDarkMode, onSave, onRemove }: CatalogChildEditorProps) {
  const [buffer, setBuffer] = useState(() => JSON.stringify(value, null, JSON_SPACING));
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setBuffer(JSON.stringify(value, null, JSON_SPACING));
  }, [value]);

  const handleReset = () => {
    setBuffer(JSON.stringify(value, null, JSON_SPACING));
    setStatus('idle');
    setErrorMessage(null);
  };

  const handleSave = async () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(buffer || 'null');
    } catch (error) {
      setStatus('error');
      setErrorMessage(getErrorMessage(error, 'Invalid JSON. Make sure the child value is valid JSON.'));
      return;
    }
    setStatus('saving');
    setErrorMessage(null);
    try {
      await onSave(parsed);
      setStatus('success');
      window.setTimeout(() => setStatus('idle'), 1500);
    } catch (error) {
      setStatus('error');
      setErrorMessage(getErrorMessage(error, 'Failed to update child value.'));
    }
  };

  const handleRemove = async () => {
    const confirmed = window.confirm(`Remove “${childKey}” from ${catalogKey}? This cannot be undone.`);
    if (!confirmed) {
      return;
    }
    setStatus('saving');
    setErrorMessage(null);
    try {
      await onRemove();
      setStatus('success');
      window.setTimeout(() => setStatus('idle'), 1500);
    } catch (error) {
      setStatus('error');
      setErrorMessage(getErrorMessage(error, 'Failed to remove child value.'));
    }
  };

  const cardClass = isDarkMode ? 'bg-slate-900/60 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200';
  const buttonPrimaryClass = isDarkMode
    ? 'inline-flex items-center gap-2 rounded-lg bg-blue-500 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-slate-900'
    : 'inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-white';
  const buttonSecondaryClass = isDarkMode
    ? 'inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-1.5 text-sm font-semibold text-slate-200 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-600 focus:ring-offset-2 focus:ring-offset-slate-900'
    : 'inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-white';
  const buttonDangerClass = isDarkMode
    ? 'inline-flex items-center gap-2 rounded-lg bg-rose-500 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:ring-offset-2 focus:ring-offset-slate-900'
    : 'inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:ring-offset-2 focus:ring-offset-white';

  return (
    <div className={`rounded-2xl p-4 ring-1 ${cardClass}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <h5 className="text-sm font-semibold">
            Child: <span className="font-mono text-xs">{childKey}</span>
          </h5>
          <p className={`${isDarkMode ? 'text-slate-300' : 'text-slate-500'} text-xs`}>
            Editing <code className="font-mono text-xs">catalogs/{catalogKey}/{childKey}</code>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={handleReset} className={buttonSecondaryClass} disabled={status === 'saving'}>
            <RefreshCcw className="h-4 w-4" aria-hidden="true" />
            Reset
          </button>
          <button type="button" onClick={handleSave} className={buttonPrimaryClass} disabled={status === 'saving'}>
            {status === 'saving' ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            Save
          </button>
          <button type="button" onClick={handleRemove} className={buttonDangerClass} disabled={status === 'saving'}>
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Delete
          </button>
        </div>
      </div>
      <textarea
        className={`${isDarkMode ? 'bg-slate-950 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200'} h-36 w-full rounded-xl border-none px-3 py-2 font-mono text-sm leading-6 ring-1 focus:outline-none focus:ring-2 focus:ring-blue-400`}
        value={buffer}
        onChange={event => setBuffer(event.target.value)}
        spellCheck={false}
      />
      {status === 'success' ? (
        <p className="mt-2 text-sm font-medium text-green-500">Saved.</p>
      ) : null}
      {status === 'error' && errorMessage ? (
        <p className="mt-2 text-sm font-medium text-rose-500">{errorMessage}</p>
      ) : null}
    </div>
  );
}

type AddChildFormProps = {
  catalogKey: string;
  isDarkMode: boolean;
  onAdd: (childKey: string, value: unknown) => Promise<void>;
  existingKeys: string[];
};

function AddChildForm({ catalogKey, isDarkMode, onAdd, existingKeys }: AddChildFormProps) {
  const [childKey, setChildKey] = useState('');
  const [jsonValue, setJsonValue] = useState('');
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const key = childKey.trim();
    if (!key) {
      setErrorMessage('Child key is required.');
      setStatus('error');
      return;
    }
    if (existingKeys.includes(key)) {
      setErrorMessage('That child key already exists.');
      setStatus('error');
      return;
    }
    let parsed: unknown = {};
    try {
      parsed = jsonValue.trim() ? JSON.parse(jsonValue) : {};
    } catch (error) {
      setStatus('error');
      setErrorMessage(getErrorMessage(error, 'Invalid JSON. Enter valid JSON or leave blank for an empty object.'));
      return;
    }
    setStatus('saving');
    setErrorMessage(null);
    try {
      await onAdd(key, parsed);
      setStatus('success');
      setChildKey('');
      setJsonValue('');
      window.setTimeout(() => setStatus('idle'), 1500);
    } catch (error) {
      setStatus('error');
      setErrorMessage(getErrorMessage(error, 'Failed to add child value.'));
    }
  };

  const cardClass = isDarkMode ? 'bg-slate-900/60 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200';
  const buttonPrimaryClass = isDarkMode
    ? 'inline-flex items-center gap-2 rounded-lg bg-blue-500 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-slate-900'
    : 'inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-white';

  return (
    <form onSubmit={handleSubmit} className={`rounded-2xl p-4 ring-1 ${cardClass}`}>
      <h5 className="mb-2 text-sm font-semibold">Add child to <span className="font-mono text-xs">{catalogKey}</span></h5>
      <div className="mb-3 grid gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label htmlFor={`child-key-${catalogKey}`} className="text-xs font-semibold uppercase tracking-wide text-blue-400">
            Child key
          </label>
          <input
            id={`child-key-${catalogKey}`}
            className={`${isDarkMode ? 'bg-slate-950 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200'} w-full rounded-lg px-3 py-2 text-sm ring-1 focus:outline-none focus:ring-2 focus:ring-blue-400`}
            value={childKey}
            onChange={event => setChildKey(event.target.value)}
            placeholder="e.g. dog"
            disabled={status === 'saving'}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold uppercase tracking-wide text-blue-400" htmlFor={`child-json-${catalogKey}`}>
            Value (JSON)
          </label>
          <textarea
            id={`child-json-${catalogKey}`}
            className={`${isDarkMode ? 'bg-slate-950 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200'} h-32 w-full rounded-lg px-3 py-2 font-mono text-xs leading-6 ring-1 focus:outline-none focus:ring-2 focus:ring-blue-400`}
            value={jsonValue}
            onChange={event => setJsonValue(event.target.value)}
            placeholder='{"label":"Dog","value":"Dog"}'
            spellCheck={false}
            disabled={status === 'saving'}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button type="submit" className={buttonPrimaryClass} disabled={status === 'saving'}>
          {status === 'saving' ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Plus className="h-4 w-4" aria-hidden="true" />
          )}
          Add child
        </button>
        {status === 'success' ? (
          <span className="text-sm font-medium text-green-500">Added.</span>
        ) : null}
        {status === 'error' && errorMessage ? (
          <span className="text-sm font-medium text-rose-500">{errorMessage}</span>
        ) : null}
      </div>
    </form>
  );
}

type CatalogSectionProps = {
  catalogKey: string;
  value: unknown;
  isDarkMode: boolean;
  onUpdateRoot: (nextValue: unknown) => Promise<void>;
  onUpdateChild: (childKey: string, nextValue: unknown) => Promise<void>;
  onRemoveChild: (childKey: string) => Promise<void>;
  onDeleteCatalog: () => Promise<void>;
  onAddChild: (childKey: string, value: unknown) => Promise<void>;
};

function CatalogSection({
  catalogKey,
  value,
  isDarkMode,
  onUpdateRoot,
  onUpdateChild,
  onRemoveChild,
  onDeleteCatalog,
  onAddChild,
}: CatalogSectionProps) {
  const objectLike = value !== null && typeof value === 'object';
  const childEntries = useMemo(() => {
    if (!objectLike) {
      return [];
    }
    return Object.entries(value as CatalogRecord).sort(([a], [b]) => a.localeCompare(b));
  }, [objectLike, value]);

  const handleDeleteCatalog = async () => {
    const confirmed = window.confirm(`Delete the entire “${catalogKey}” catalog? This cannot be undone.`);
    if (!confirmed) {
      return;
    }
    await onDeleteCatalog();
  };

  const sectionHeaderClass = isDarkMode ? 'text-blue-300' : 'text-blue-600';
  const buttonDangerClass = isDarkMode
    ? 'inline-flex items-center gap-2 rounded-lg bg-rose-500 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:ring-offset-2 focus:ring-offset-slate-900'
    : 'inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:ring-offset-2 focus:ring-offset-white';
  const mutedTextClass = isDarkMode ? 'text-slate-300' : 'text-slate-600';

  return (
    <section className="space-y-4 rounded-3xl border border-transparent bg-transparent p-1">
      <div className="flex items-center justify-between rounded-2xl bg-blue-500/10 px-4 py-3">
        <div>
          <h3 className={`text-lg font-semibold ${sectionHeaderClass}`}>{catalogKey}</h3>
          <p className={`${mutedTextClass} text-sm`}>
            Path: <code className="font-mono text-xs">catalogs/{catalogKey}</code>
          </p>
        </div>
        <button type="button" onClick={handleDeleteCatalog} className={buttonDangerClass}>
          <Trash2 className="h-4 w-4" aria-hidden="true" />
          Delete catalog
        </button>
      </div>

      <RootValueEditor catalogKey={catalogKey} value={value} isDarkMode={isDarkMode} onSave={onUpdateRoot} />

      {objectLike ? (
        <>
          <div className="flex flex-col gap-4">
            {childEntries.map(([childKey, childValue]) => (
              <CatalogChildEditor
                key={childKey}
                catalogKey={catalogKey}
                childKey={childKey}
                value={childValue}
                isDarkMode={isDarkMode}
                onSave={nextValue => onUpdateChild(childKey, nextValue)}
                onRemove={() => onRemoveChild(childKey)}
              />
            ))}
          </div>
          <AddChildForm
            catalogKey={catalogKey}
            isDarkMode={isDarkMode}
            onAdd={onAddChild}
            existingKeys={childEntries.map(([childKey]) => childKey)}
          />
        </>
      ) : (
        <p className={`${mutedTextClass} rounded-2xl bg-yellow-500/10 px-4 py-3 text-sm`}>
          This catalog&apos;s value is not an object. Update the root JSON above to convert it into an object if you want
          to manage child entries here.
        </p>
      )}
    </section>
  );
}

type AddCatalogFormProps = {
  isDarkMode: boolean;
  onAddCatalog: (catalogKey: string, value: unknown) => Promise<void>;
  existingCatalogKeys: string[];
};

function AddCatalogForm({ isDarkMode, onAddCatalog, existingCatalogKeys }: AddCatalogFormProps) {
  const [catalogKey, setCatalogKey] = useState('');
  const [jsonValue, setJsonValue] = useState('');
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const key = catalogKey.trim();
    if (!key) {
      setStatus('error');
      setErrorMessage('Catalog key is required.');
      return;
    }
    if (existingCatalogKeys.includes(key)) {
      setStatus('error');
      setErrorMessage('That catalog already exists.');
      return;
    }
    let parsed: unknown = {};
    try {
      parsed = jsonValue.trim() ? JSON.parse(jsonValue) : {};
    } catch (error) {
      setStatus('error');
      setErrorMessage(getErrorMessage(error, 'Invalid JSON. Enter valid JSON or leave blank for an empty object.'));
      return;
    }
    setStatus('saving');
    setErrorMessage(null);
    try {
      await onAddCatalog(key, parsed);
      setStatus('success');
      setCatalogKey('');
      setJsonValue('');
      window.setTimeout(() => setStatus('idle'), 1500);
    } catch (error) {
      setStatus('error');
      setErrorMessage(getErrorMessage(error, 'Failed to create catalog.'));
    }
  };

  const cardClass = isDarkMode ? 'bg-slate-950 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200';
  const buttonPrimaryClass = isDarkMode
    ? 'inline-flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-slate-900'
    : 'inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-white';
  const mutedTextClass = isDarkMode ? 'text-slate-300' : 'text-slate-600';

  return (
    <form onSubmit={handleSubmit} className={`rounded-3xl p-6 ring-1 ${cardClass}`}>
      <div className="mb-4">
        <h2 className="text-xl font-semibold">Add a catalog</h2>
        <p className={`${mutedTextClass} mt-1 text-sm`}>
          Create a new top-level catalog under <code className="font-mono text-xs">catalogs/*</code>.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="new-catalog-key" className="text-xs font-semibold uppercase tracking-wide text-blue-400">
            Catalog key
          </label>
          <input
            id="new-catalog-key"
            className={`${isDarkMode ? 'bg-slate-900 text-slate-100 ring-slate-700' : 'bg-slate-50 text-slate-900 ring-slate-300'} w-full rounded-lg px-3 py-2 text-sm ring-1 focus:outline-none focus:ring-2 focus:ring-blue-400`}
            value={catalogKey}
            onChange={event => setCatalogKey(event.target.value)}
            placeholder="e.g. species"
            disabled={status === 'saving'}
          />
          <p className={`${mutedTextClass} text-xs`}>Use lowercase letters, numbers, and dashes for consistency.</p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="new-catalog-json" className="text-xs font-semibold uppercase tracking-wide text-blue-400">
            Initial value (JSON)
          </label>
          <textarea
            id="new-catalog-json"
            className={`${isDarkMode ? 'bg-slate-900 text-slate-100 ring-slate-700' : 'bg-slate-50 text-slate-900 ring-slate-300'} h-32 w-full rounded-lg px-3 py-2 font-mono text-xs leading-6 ring-1 focus:outline-none focus:ring-2 focus:ring-blue-400`}
            value={jsonValue}
            onChange={event => setJsonValue(event.target.value)}
            placeholder="{}"
            spellCheck={false}
            disabled={status === 'saving'}
          />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button type="submit" className={buttonPrimaryClass} disabled={status === 'saving'}>
          {status === 'saving' ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Plus className="h-4 w-4" aria-hidden="true" />
          )}
          Create catalog
        </button>
        {status === 'success' ? (
          <span className="text-sm font-medium text-green-500">Catalog created.</span>
        ) : null}
        {status === 'error' && errorMessage ? (
          <span className="text-sm font-medium text-rose-500">{errorMessage}</span>
        ) : null}
      </div>
    </form>
  );
}

export default function Catalogs() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [catalogs, setCatalogs] = useState<Record<string, unknown>>({});
  const [catalogsLoading, setCatalogsLoading] = useState(true);
  const [catalogsError, setCatalogsError] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('theme') === 'dark';
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setAuthChecked(true);
      if (!user) {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/catalogs' },
        });
      }
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark') {
      setIsDarkMode(true);
    }
    if (storedTheme === 'light') {
      setIsDarkMode(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    const profileRef = ref(database, `users/${currentUser.uid}`);
    setProfileLoading(true);
    const unsubscribe = onValue(
      profileRef,
      snapshot => {
        const value = snapshot.val() as UserProfile | null;
        if (value?.needsOrganizationSetup && !value?.superAdmin) {
          setProfileLoading(false);
          navigate('/organization/create', {
            replace: true,
            state: { returnTo: '/catalogs' },
          });
          return;
        }
        setProfile(value);
        setProfileLoading(false);
      },
      error => {
        console.warn('Failed to load profile for catalogs view', error);
        setProfile(null);
        setProfileLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, navigate]);

  const isSuperAdmin = profile?.superAdmin === true;

  useEffect(() => {
    if (!isSuperAdmin) {
      setCatalogs({});
      setCatalogsLoading(false);
      setCatalogsError(null);
      return;
    }

    const catalogsRef = ref(database, 'catalogs');
    setCatalogsLoading(true);
    setCatalogsError(null);

    const unsubscribe = onValue(
      catalogsRef,
      snapshot => {
        const value = snapshot.val();
        if (value && typeof value === 'object') {
          setCatalogs(value as Record<string, unknown>);
        } else {
          setCatalogs({});
        }
        setCatalogsLoading(false);
      },
      error => {
        console.warn('Failed to load catalogs data', error);
        setCatalogs({});
        setCatalogsError('Failed to load catalogs. Please try again.');
        setCatalogsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isSuperAdmin]);

  const handleThemeToggle = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', next ? 'dark' : 'light');
      }
      return next;
    });
  };

  const handleSignIn = () => {
    navigate('/signin', { state: { returnTo: '/catalogs' } });
  };

  const handleCreateAccount = () => {
    navigate('/register', { state: { returnTo: '/catalogs' } });
  };

  const handleLogout = () => {
    void signOut(auth)
      .then(() => {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/catalogs' },
        });
      })
      .catch(error => {
        console.warn('Failed to sign out', error);
      });
  };

  const catalogEntries = useMemo(() => Object.entries(catalogs).sort(([a], [b]) => a.localeCompare(b)), [catalogs]);

  const handleAddCatalog = useCallback(
    async (catalogKey: string, value: unknown) => {
      const catalogRef = ref(database, `catalogs/${catalogKey}`);
      await set(catalogRef, value);
    },
    []
  );

  const handleUpdateRoot = useCallback(
    async (catalogKey: string, value: unknown) => {
      const catalogRef = ref(database, `catalogs/${catalogKey}`);
      await set(catalogRef, value);
    },
    []
  );

  const handleUpdateChild = useCallback(
    async (catalogKey: string, childKey: string, value: unknown) => {
      const childRef = ref(database, `catalogs/${catalogKey}/${childKey}`);
      await set(childRef, value);
    },
    []
  );

  const handleRemoveChild = useCallback(async (catalogKey: string, childKey: string) => {
    const childRef = ref(database, `catalogs/${catalogKey}/${childKey}`);
    await set(childRef, null);
  }, []);

  const handleDeleteCatalog = useCallback(async (catalogKey: string) => {
    const catalogRef = ref(database, `catalogs/${catalogKey}`);
    await set(catalogRef, null);
  }, []);

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900 text-slate-100">
        <div className="flex items-center gap-3 text-lg font-semibold">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Checking your access…
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  const displayName =
    profile?.displayName?.trim() ||
    currentUser.displayName?.trim() ||
    currentUser.email ||
    'there';
  const hasOrganizationAccess = Boolean(profile?.organizationId || profile?.superAdmin);

  return (
    <DashboardLayout
      isDarkMode={isDarkMode}
      onToggleTheme={handleThemeToggle}
      currentUser={currentUser}
      onSignIn={handleSignIn}
      onCreateAccount={handleCreateAccount}
      onLogout={handleLogout}
      needsOrganizationSetup={profile?.needsOrganizationSetup}
      hasOrganizationAccess={hasOrganizationAccess}
      organizationMembershipStatus={profile?.organizationMembershipStatus}
    >
      <div className="space-y-6">
        <header className="rounded-3xl bg-blue-500/10 px-6 py-5">
          <h1 className="text-2xl font-semibold text-blue-500">Catalogs</h1>
          <p className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'} mt-2 text-sm`}>
            Manage top-level catalog entries like species and their child data. Values are stored as JSON in Firebase.
          </p>
        </header>

        {profileLoading ? (
          <div className="flex items-center gap-3 rounded-3xl bg-yellow-500/10 px-5 py-3 text-yellow-600">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading your profile…
          </div>
        ) : null}

        {!isSuperAdmin && !profileLoading ? (
          <div className={`${isDarkMode ? 'bg-rose-500/10 text-rose-100 ring-rose-500/40' : 'bg-rose-100 text-rose-700 ring-rose-300'} rounded-3xl px-6 py-5 ring-1`}>
            <h2 className="text-lg font-semibold">Access restricted</h2>
            <p className="mt-2 text-sm">
              Only super administrators can manage catalogs. If you need access, contact your platform administrator.
            </p>
          </div>
        ) : null}

        {isSuperAdmin ? (
          <>
            <AddCatalogForm
              isDarkMode={isDarkMode}
              onAddCatalog={handleAddCatalog}
              existingCatalogKeys={catalogEntries.map(([key]) => key)}
            />

            {catalogsLoading ? (
              <div className="flex items-center gap-3 rounded-3xl bg-blue-500/10 px-5 py-3 text-blue-500">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Loading catalogs…
              </div>
            ) : null}

            {catalogsError ? (
              <div className={`${isDarkMode ? 'bg-rose-500/10 text-rose-100 ring-rose-500/40' : 'bg-rose-100 text-rose-700 ring-rose-300'} rounded-3xl px-6 py-5 ring-1`}>
                <p className="text-sm font-medium">{catalogsError}</p>
              </div>
            ) : null}

            {!catalogsLoading && !catalogsError && catalogEntries.length === 0 ? (
              <div className={`${isDarkMode ? 'bg-slate-900/60 text-slate-100 ring-slate-800' : 'bg-slate-100 text-slate-600 ring-slate-200'} rounded-3xl px-6 py-5 text-sm ring-1`}>
                No catalogs found. Add one above to get started.
              </div>
            ) : null}

            <div className="space-y-10">
              {catalogEntries.map(([catalogKey, catalogValue]) => (
                <CatalogSection
                  key={catalogKey}
                  catalogKey={catalogKey}
                  value={catalogValue}
                  isDarkMode={isDarkMode}
                  onUpdateRoot={nextValue => handleUpdateRoot(catalogKey, nextValue)}
                  onUpdateChild={(childKey, nextValue) => handleUpdateChild(catalogKey, childKey, nextValue)}
                  onRemoveChild={childKey => handleRemoveChild(catalogKey, childKey)}
                  onDeleteCatalog={() => handleDeleteCatalog(catalogKey)}
                  onAddChild={(childKey, childValue) => handleUpdateChild(catalogKey, childKey, childValue)}
                />
              ))}
            </div>
          </>
        ) : null}

        <footer className={`${isDarkMode ? 'text-slate-400' : 'text-slate-500'} text-sm`}>
          Signed in as <span className="font-medium text-blue-500">{displayName}</span>
          {isSuperAdmin ? ' (super admin)' : null}.
        </footer>
      </div>
    </DashboardLayout>
  );
}
